import {
  Alert,
  Button,
  Card,
  CardBody,
  EmptyState,
  formatDate,
  FormField,
  Input,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableLoading,
  TableRow,
  useToast,
  VisuallyHidden,
} from '@hkdevs/design-system';
import { useId, useState } from 'react';
import { ApiError } from '../../api/client';
import { useCreateProduct, useProducts, type ProductType } from '../../api/queries/products';
import { PageLayout } from '../../shell/Page';
import { withReference } from '../../api/withReference';

/** "7 fields", "1 field", "No fields". */
export const fieldCountLabel = (count: number) =>
  count === 0 ? 'No fields' : `${count} ${count === 1 ? 'field' : 'fields'}`;

/** What's wrong with a product type's name, given the others' names. */
export function validateTypeName(name: string, others: readonly string[]): string | undefined {
  const trimmed = name.trim();
  if (!trimmed) return 'Enter a name.';
  if (others.some((other) => other.trim().toLowerCase() === trimmed.toLowerCase()))
    return `There’s already a product type called ${trimmed}.`;
  return undefined;
}

/**
 * Products (pc-dbb.20, pc-50i.5): the product types, each with its field count and when
 * it last changed, and Add product type, which opens the new type.
 */
export function ProductsPage({ onOpen }: { onOpen: (id: string) => void }) {
  const products = useProducts();
  const [adding, setAdding] = useState(false);

  let body;
  if (products.isError) {
    body = (
      <Alert
        tone="danger"
        title="Product types couldn’t be loaded"
        action={
          <Button size="sm" variant="secondary" onClick={() => void products.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  } else if (products.data?.length === 0) {
    body = (
      <Card>
        <CardBody>
          <EmptyState
            title="No product types"
            description="Add a product type, such as Solar or Roofing, then choose the fields its line items show on a check."
            icon="package"
            headingLevel={2}
            actions={
              <Button size="sm" leadingIcon="plus" onClick={() => setAdding(true)}>
                Add product type
              </Button>
            }
          />
        </CardBody>
      </Card>
    );
  } else {
    body = (
      <Card flush>
        <Table aria-label="Product types" responsive="cards" busy={!products.data}>
          <TableHead>
            <TableCell>Product type</TableCell>
            <TableCell>Fields</TableCell>
            <TableCell>Last changed</TableCell>
            <TableCell width="44px">
              <VisuallyHidden>Open</VisuallyHidden>
            </TableCell>
          </TableHead>
          <TableBody>
            {!products.data ? (
              <TableLoading colSpan={4} rows={4} label="Loading product types" />
            ) : (
              products.data.map((type) => (
                <TableRow key={type.id} onClick={() => onOpen(type.id)} label={`Open ${type.name}`}>
                  <TableCell rowHeader tone="strong">
                    {type.name}
                  </TableCell>
                  <TableCell label="Fields" tone="muted">
                    {fieldCountLabel(type.fieldCount)}
                  </TableCell>
                  <TableCell label="Last changed" tone="muted">
                    {formatDate(type.updatedAt, 'date')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    );
  }

  return (
    <PageLayout
      page="products"
      actions={
        <Button size="sm" leadingIcon="plus" onClick={() => setAdding(true)}>
          Add product type
        </Button>
      }
    >
      {body}
      {adding && (
        <AddProductTypeModal
          others={(products.data ?? []).map((type) => type.name)}
          onClose={() => setAdding(false)}
          onAdded={(type) => {
            setAdding(false);
            onOpen(type.id);
          }}
        />
      )}
    </PageLayout>
  );
}

/** Add product type: its name. Its fields come next, on its page. */
function AddProductTypeModal({
  others,
  onClose,
  onAdded,
}: {
  others: readonly string[];
  onClose: () => void;
  onAdded: (type: ProductType) => void;
}) {
  const { toast } = useToast();
  const formId = useId();
  const create = useCreateProduct();
  const [name, setName] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>();
  const error = serverError ?? (showErrors ? validateTypeName(name, others) : undefined);

  const submit = () => {
    setShowErrors(true);
    setServerError(undefined);
    if (validateTypeName(name, others)) return;
    create.mutate(name.trim(), {
      onSuccess: onAdded,
      onError: (failure) => {
        const taken = failure instanceof ApiError && failure.status === 409;
        if (taken) setServerError(failure.problem.errors?.[0]?.message ?? failure.message);
        else
          toast(
            withReference(
              {
                tone: 'danger',
                title: 'The product type couldn’t be added',
                description: 'Check your connection and try again.',
              },
              failure,
            ),
          );
      },
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Add product type"
      description="You choose its fields next."
      dismissable={!create.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={create.isPending} loadingText="Adding…">
            Add product type
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <FormField label="Name" required error={error} hint="As it shows on checks: “Solar”.">
          <Input
            value={name}
            autoComplete="off"
            disabled={create.isPending}
            onChange={(event) => setName(event.target.value)}
          />
        </FormField>
      </form>
    </Modal>
  );
}
